/**
 * Member password policy — mirrors sristi-green-backend `domain/security/PasswordPolicy.ts` rule for rule:
 * 8–72 characters (code points) and at most 72 UTF-8 bytes, one Unicode letter, one digit, not the member's phone.
 * The server stays authoritative (422 PASSWORD_POLICY); this only gives instant feedback.
 */
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 72;

export type PasswordRule = 'LENGTH' | 'LETTER' | 'DIGIT' | 'NOT_PHONE';

const utf8Bytes = (s: string): number => new TextEncoder().encode(s).length;

/** Rules the password currently breaks (empty list = acceptable). */
export function checkPasswordPolicy(password: string, phone: string): PasswordRule[] {
  const violations: PasswordRule[] = [];
  const chars = [...password].length;
  if (chars < PASSWORD_MIN_LENGTH || chars > PASSWORD_MAX_LENGTH || utf8Bytes(password) > PASSWORD_MAX_LENGTH) {
    violations.push('LENGTH');
  }
  if (!/\p{L}/u.test(password)) violations.push('LETTER');
  if (!/\d/.test(password)) violations.push('DIGIT');
  if (phone && password === phone) violations.push('NOT_PHONE');
  return violations;
}

/** UI-only strength meter: 0 empty/invalid, 1 weak, 2 fair, 3 strong. Never gates submission. */
export function passwordStrength(password: string, phone: string): 0 | 1 | 2 | 3 {
  if (!password || checkPasswordPolicy(password, phone).length > 0) return password ? 1 : 0;
  const classes = [/\p{Ll}/u, /\p{Lu}/u, /\d/, /[^\p{L}\d]/u].filter((re) => re.test(password)).length;
  const len = [...password].length;
  if (len >= 12 && classes >= 3) return 3;
  return len >= 10 || classes >= 3 ? 2 : 1;
}
